<template>
  <div
    v-if="isOpen"
    class="manual-backdrop"
    @click.self="close">
    <section
      class="manual-panel"
      :class="{ 'manual-panel-pdf': !!url }"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manual-title">
      <header class="manual-header">
        <div>
          <p class="manual-kicker">Ayuda en línea</p>
          <h2 id="manual-title">
            {{ manual ? `Manual de usuario — ${manual.title}` : 'Ayuda' }}
          </h2>
        </div>
        <div class="manual-actions">
          <a
            v-if="url"
            class="manual-link"
            :href="url"
            target="_blank"
            rel="noopener">
            Abrir PDF
          </a>
          <button
            type="button"
            class="manual-close"
            @click="close">
            Cerrar
          </button>
        </div>
      </header>
      <iframe
        v-if="url"
        class="manual-frame"
        :src="url"
        :title="manual?.title"/>
      <p
        v-else
        class="manual-empty">
        Este módulo todavía no tiene manual de usuario.
      </p>
      <footer class="manual-footer">F1 abre o cierra la ayuda · Esc cierra</footer>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { useUserManual } from '@/shared/composables/useUserManual.ts';

const { isOpen, manual, url, close, toggle } = useUserManual();

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'F1') {
    event.preventDefault();
    event.stopPropagation();
    toggle();
    return;
  }
  if (event.key === 'Escape' && isOpen.value) {
    event.preventDefault();
    event.stopPropagation();
    close();
  }
};

onMounted(() => {
  window.addEventListener('keydown', onKeyDown, true);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown, true);
});
</script>

<style scoped>
.manual-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(22, 18, 16, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.manual-panel {
  width: min(560px, 100%);
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
}

.manual-panel-pdf {
  width: min(1100px, 100%);
  height: min(86vh, 900px);
}

.manual-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 0.9rem 1.1rem;
  background: #322a26;
  color: #fff;
}

.manual-kicker {
  margin: 0;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b7d7a8;
}

.manual-header h2 {
  margin: 0.15rem 0 0;
  font-size: 1.05rem;
  font-weight: 650;
}

.manual-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.manual-link,
.manual-close {
  border: 0;
  border-radius: 8px;
  padding: 0.45rem 0.8rem;
  font-size: 0.85rem;
  text-decoration: none;
  cursor: pointer;
}

.manual-link {
  background: #4a8c2b;
  color: #fff;
}

.manual-close {
  background: #fff;
  color: #322a26;
}

.manual-frame {
  flex: 1;
  width: 100%;
  border: 0;
  background: #f4f1ee;
}

.manual-empty {
  margin: 0;
  padding: 1.5rem 1.25rem 0.5rem;
  color: #322a26;
}

.manual-footer {
  padding: 0.55rem 1.1rem 0.75rem;
  font-size: 0.78rem;
  color: #6b625c;
}
</style>
